import { useEffect, useRef, useState } from 'react';
export async function api<T>(url: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(url, { ...options, credentials: 'same-origin' });
  const body = await response.text();
  let data: Record<string, unknown> | T | null = null;
  try {
    data = body ? JSON.parse(body) : null;
  } catch {
    throw Object.assign(new Error('The server returned an invalid response'), { status: response.status });
  }
  if (!response.ok)
    throw Object.assign(new Error((data as Record<string, unknown> | null)?.error as string ?? 'Unable to complete the request'), {
      status: response.status,
      issues: (data as Record<string, unknown> | null)?.issues,
    });
  if (data === null) throw Object.assign(new Error('The server returned an empty response'), { status: response.status });
  return data as T;
}
export function useResource<T>(url: string, initial?: { url: string; data: T }) {
  const [state, setState] = useState<{ url: string; data?: T; loading: boolean; error: string }>({
    url,
    // Keep server-provided content in the initial HTML even when the browser adds
    // a country query. The first client fetch refreshes it for that country.
    data: initial?.data,
    loading: initial?.url !== url,
    error: '',
  });
  const [nonce, setNonce] = useState(0);
  const first = useRef(initial?.url === url);
  const preserveInitialFetch = useRef(Boolean(initial && initial.url !== url));
  useEffect(() => {
    if (first.current && nonce === 0) {
      first.current = false;
      return;
    }
    const abort = new AbortController();
    setState({
      url,
      data: preserveInitialFetch.current ? initial?.data : undefined,
      loading: true,
      error: '',
    });
    preserveInitialFetch.current = false;
    api<T>(url, { signal: abort.signal })
      .then((data) => {
        if (!abort.signal.aborted) setState({ url, data, loading: false, error: '' });
      })
      .catch((e) => {
        // A failed client-side refresh must not erase valid server-rendered content.
        if (!abort.signal.aborted) setState((current) => ({ ...current, url, loading: false, error: e.message }));
      });
    return () => abort.abort();
  }, [url, nonce]);
  // Lets an in-place edit pull the saved record back into the page it was made on.
  const reload = () => setNonce((n) => n + 1);
  return { ...(state.url === url ? state : { url, loading: true, error: '' }), reload };
}
